import axios from 'axios';
import { API_URL } from '../config/api';

// Create axios instance with CSRF token handling
const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true // Enable cookies for CSRF token
});

// Cache CSRF token to avoid repeated requests
let csrfToken = null;
let csrfPromise = null;

const getCsrfToken = async () => {
  if (csrfToken) return csrfToken;
  if (csrfPromise) return csrfPromise;
  
  csrfPromise = axios.get(`${API_URL}/api/csrf-token`, { withCredentials: true }).then(response => {
    csrfToken = response.data.csrfToken;
    csrfPromise = null;
    return csrfToken;
  }).catch(error => {
    csrfPromise = null;
    throw error;
  });
  
  return csrfPromise;
};

// Request interceptor to add CSRF token
apiClient.interceptors.request.use(
  async (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
      try {
        const csrfTokenValue = await getCsrfToken();
        config.headers['X-CSRF-Token'] = csrfTokenValue;
      } catch (error) {
        console.warn('Failed to get CSRF token:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const clearCsrfToken = () => {
  csrfToken = null;
  csrfPromise = null;
};

const isCsrfError = (error) =>
  error.response?.status === 403 &&
  (error.response?.data?.code === 'EBADCSRFTOKEN' || error.response?.data?.error === 'Invalid CSRF token');

// Response interceptor to handle CSRF token expiration: refresh the token and retry once
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isCsrfError(error)) {
      clearCsrfToken();
      if (error.config && !error.config._csrfRetried) {
        error.config._csrfRetried = true;
        return apiClient(error.config);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
export { getCsrfToken, clearCsrfToken };